/**
 * @typedef {Object} Currency
 *
 * @property {string} code
 * @property {string} name
 * @property {string} symbol
 * @property {number} decimalDigits
 */

/**
 * @typedef {Object} Fees
 *
 * @property {number} year
 * @property {number} tuition
 * @property {number} levies
 * @property {number} extraCosts
 * @property {number} total
 * @property {string} period
 * @property {Currency} currency
 */


(function() {
    'use strict';

    angular.module( APP_ID )

        .component( 'fees', {
            bindings: {
                /** @type {Fees} */
                fees:           '<',

                /** @type {string} */
                programmeCode:  '<',

                /** @type {boolean} */
                compactView:    '@',

                /** @type {function} */
                onModelReady:   '&'
            },
            templateUrl: 'app/views/partials/widgets/fees.html',
            controller:   FeesController,
            controllerAs: 'vm'
        })

        .component( 'feesMinified', {
            bindings: {
                /** @type {Fees} */
                fees:           '<',

                /** @type {boolean} */
                skipConversion: '@',

                /** @type {string} */
                programmeCode:  '<',

                /** @type {function} */
                onModelReady:   '&'
            },
            templateUrl: 'app/views/partials/widgets/fees-minified.html',
            controller:   FeesController,
            controllerAs: 'vm'
        });


     /**
      * Shows fees for the given programme, including fees breakdown, related
      * links and currency conversion.
      * Important: In case any significant changes are made to this component,
      * please re-test according to the https://victoriauniversity.atlassian.net/wiki/spaces/WT/pages/168198145/Blackbox+testing+guide+for+Topics+Programmes#BlackboxtestingguideforTopics&Programmes-Postgraduate'summary'cards
      *
      * @attribute model - Object with fees and its metadata @type {Fees}.
      * @attribute programme-code - International API programme identifier.
      * @attribute compact-view   - Shows more 'compact' version of the fees.
      *
      * @param {ng.IQService} $q
      * @param {ng.IScope} $scope
      * @param {ng.IFilterService} $filter
      * @param {ContentSwitchingService} ContentSwitchingService
      * @param {DataService} DataService
      * @param {LoggerFactory} LoggerFactory
      * @param {SubpagesService} SubpagesService
      * @param {App} App
      * @param {Utils} Utils
      * @param {Object} TEXTS
      */
    function FeesController(
        $q,
        $scope,
        $filter,
        ContentSwitchingService,
        DataService,
        LoggerFactory,
        SubpagesService,
        App,
        Utils,
        TEXTS
    ){

        /** @type {angular.IController} */
        var vm = this;





        /* ===== PRIVATE MEMBERS ============================================ */


        /** @type {Logger} */
        var logger = LoggerFactory.createLogger( 'FeesController', false );


        /** @constant */
        var DEFAULT_INTERNATIONAL_YEAR = 2021;


        // Members
        /** @type {Object} */
        var exchangeRatesObject;

        /** @type {Promise<Fees>} */
        var feesPromise;

        /** @type {boolean} */
        var skipConversion;





        /* ===== EXPOSE TO THE SCOPE ======================================== */


        $scope._             = TEXTS.DEGREE.OVERVIEW;
        $scope._.FEES        = TEXTS.SHARED.FEES,

        $scope.SubpagesService = SubpagesService,

        $scope.tooltipHeadings = Utils.getSharedContent().headingsToolTips.asset_contents;





        /* ===== EXPOSE TO THE CONTROLLER =================================== */


        /** @const */
        vm.RESPONSE_PERIOD_FULL_PROGRAMME = 'full programme';





        /* ===== EXPOSE TO THE CONTROLLER =============================== */


        /** Members */
        /* vm.fees          = undefined; */
        vm.currenciesList   = undefined;
        vm.selectedCurrency = undefined;
        vm.exchangeRate     = undefined;

        vm.isForOverseasOnly = undefined;

        /** Methods */
        vm.updateCurrency   = updateCurrency;
        vm.Utils            = Utils;
        vm.getCurrencyFilter =  $filter( 'currency' );



        /* ===== RUN ON INSTANTIATION =================================== */


        // *MUST BE* present to make `ui-select` directive work properly
        vm.selectConfig           = {
            template:       'select2',
            templateSelection: function( selectedObject ) {
                // Keep the placeholder unchanged
                if ( selectedObject.id === '' ) return selectedObject.text;

                // Return selected currencies symbol
                return vm.currenciesList[ selectedObject.id ].symbol;
            },
            dropdownAutoWidth: true
        }


        /** @constructor */
        function init(){
            initDependencies();
            setFallbacksFromMetadata();
            prepareModel();
            setWatchers();
        };

        vm.$onInit = init;





        /* ===== CONTROLLER'S PUBLIC METHODS ============================ */


        /**
         * Updates a currency, saves the new preference into the settings
         * (~cookies) and updates exchange rate accordingly.
         *
         * @returns {void}
         */
        function updateCurrency() {
            if ( !vm.currenciesList.hasOwnProperty( vm.selectedCurrency.code )) {
                logger.error( "Trying to save a currency `%s` that does not exist.", vm.selectedCurrency.code );
                return;
            }

            logger.debug( "Updating to new currency `%s`...", vm.selectedCurrency.code );
            ContentSwitchingService.userSettings.preferredCurrencyCode = vm.selectedCurrency.code;

            logger.debug( "Saving new currency `%s` to the cookie...", vm.selectedCurrency.code );
            ContentSwitchingService.saveSettingsToCookie();

            refreshExchangeRate();
        }





        /* ===== PRIVATE FUNCTIONS ====================================== */


        function initDependencies() {
            vm.isFeesError      = false;
            vm.isInternational  = ContentSwitchingService.isInternational();

            // Full view or compact view?
            vm.isFullView = angular.isUndefined( vm.compactView );

            // Should we skip the currency conversion completely?
            skipConversion = angular.isDefined( vm.skipConversion );
        }


        /**
         * If the mandatory data are not supplied to the component, it tries
         * to load them from it's parental qualification attributes (~major,
         * subqualification, degrees or postgrads).
         */
        function setFallbacksFromMetadata() {
            if ( angular.isUndefined( vm.programmeCode ) ) vm.programmeCode = Utils.retrieveMetadataRecursively( $scope, 'internationalApiCode' );

            if ( angular.isUndefined( vm.fees ) ) setFeesFallback();
        }


        /**
         * If the fees model was not supplied to the Component, retrieve
         * the fees from the International API (for International content)
         * or from the Content API (for domestic content).
         */
        function setFeesFallback() {
            if ( vm.isInternational ) {
                feesPromise = getFallbackFeesPromise();
            } else {
                vm.fees = Utils.retrieveMetadataRecursively( $scope, 'fees' );

                if ( !vm.fees ) {
                    logger.error( "Domestic fees were NOT found in any of the parental `metadata` attribute." );
                }
            }
        }


        /**
         * Wraps a standard {Promsie} with a response from the
         * International API into another {Promise} that parses
         * the fees out and returns them in a correct format.
         *
         * @returns {Promise}
         */
        function getFallbackFeesPromise() {
            var defer = $q.defer();

            getProgrammePromise().then( function( programmeResponse ) {
                var fees = getFeesFromProgrammeResponse( programmeResponse.data.programme );

                if ( fees ) {
                    defer.resolve( fees );
                } else {
                    defer.reject( { message: "Cannot retrieve fees from International API.", data: programmeResponse } );
                }
            });

            return defer.promise;
        }


        /**
         * Maps the fees from the international API to the format of
         * the Content CMS API.
         *
         * @param {Object} programmeResponse
         *
         * @returns {null|Fees}
         */
        function getFeesFromProgrammeResponse( programmeResponse ) {
            if ( !programmeResponse ) {
                if ( !vm.programmeCode ) {
                    logger.error( getLogIntro() + "No International API code has been provided. Cannot retrieve fees information.", programmeResponse );
                } else {
                    logger.error( getLogIntro() + "International API failed to return valid response (time out, malformed JSON etc.)", programmeResponse );
                }
            } else {
                try {
                    var fees = {
                        year:       programmeResponse.year || DEFAULT_INTERNATIONAL_YEAR,
                        tuition:    programmeResponse.tuitionNZD,
                        levies:     programmeResponse.leviesNZD,
                        period:     programmeResponse.feeType,
                        extraCosts: programmeResponse.additionalCostsNZD
                    };

                    logger.ok( App.isPublicMode() ? '' : getLogIntro() + "Successfully received!", fees );
                    return fees;
                } catch ( err ){
                    logger.error( "International fees COULD NOT be bound to the view-model due to app's internal parsing issue.", err );
                }
            }

            return null;
        }


        function setWatchers() {
            $scope.$watch( function(){ return ContentSwitchingService.userSettings.preferredCurrencyCode }, function( newCurrencyCode ) {

                if ( vm.currenciesList && newCurrencyCode !== vm.selectedCurrency.code ){
                    logger.debug( "Preferred currency has changed, updating fees information." );
                    vm.selectedCurrency = vm.currenciesList[ newCurrencyCode ];
                    updateCurrency();
                }
            });
        }


        /**
         * Updates exchange rate if another currency is selected.
         *
         * @returns {void}
         */
        function refreshExchangeRate() {
            if ( !vm.exchangeRate ) return;

            logger.debug( "Refreshing exchange rates." );
            updateExchangeRateForSelectedCurrency();
        }


        /**
         *  Makes requests to the API to prepare all models (~fees, currencies, exchange rates).
         */
        function prepareModel() {
            var foreignCurrencyCode = ContentSwitchingService.userSettings.preferredCurrencyCode,

            primaryRequestsPromises = {
                fees:       feesPromise,
                currencies: !skipConversion ? ContentSwitchingService.getCurrenciesPromise() : null
            };

            logger.debug( "Fetching programme information, current exchange rate for %s currency and programme information to prepare fees...", foreignCurrencyCode );

            var qualCode = Utils.retrieveMetadataRecursively( $scope, 'code' );
            vm.isForOverseasOnly = qualCode && ( qualCode === 'UGCoP' || qualCode === 'PGCoP' );

            $q.all( primaryRequestsPromises ).then( function( responses ){
                vm.fees = responses.fees || vm.fees;

                // If fees from international API are available, enhance the study year from Squiz CMS response
                if ( vm.isInternational && vm.fees ) {
                    var internationalYear = Utils.retrieveMetadataRecursively( $scope, 'fees' ).internationalYear;

                    if ( internationalYear ) vm.fees.year = internationalYear;
                }

                if ( !vm.fees ) {
                    logger.error( getLogIntro() + "Content API failed to return fees information. Fees WILL NOT be shown...", vm.fees );
                    vm.isFeesError = true;
                    return;
                }

                extendFeesModel();

                // If currencies did not get through, do not convert
                if ( responses.currencies ) {
                    vm.currenciesList   = responses.currencies;
                    vm.selectedCurrency = vm.currenciesList[ foreignCurrencyCode ];

                    initExchangeRate();
                }

                vm.isFeesError = false;
                notifyOnModelReady();
            }, function( err ) {
                logger.warn( "Unable to retrieve or process fees. Fees WILL NOT be shown...", err );

                vm.isFeesError = true;
                notifyOnModelReady();
            });

        }


        function notifyOnModelReady() {
            vm.onModelReady({ areFeesVisible: Boolean( vm.fees && !vm.isFeesError ) });
        }

        /**
         * Retrieves exchange rates from the API, given the international content
         * was requested and both (student) `levies` and `tuition` are defined.
         */
        function initExchangeRate() {
            vm.exchangeRate = undefined;

            if ( vm.fees.tuition === null ) {
                logger.warn( "Tuition (%s) is not known - Cannot show fees yet.", vm.fees.tuition, vm.fees.levies );
            } else {
                retrieveExchangeRate();
            }
        }


        function retrieveExchangeRate() {
            ContentSwitchingService.getCurrencyExchangeRatePromise().then( function( ratesObject ) {
                exchangeRatesObject     = ratesObject;

                updateExchangeRateForSelectedCurrency();
            }, function ( error ) {
                logger.warn( "Currency conversion API failed to return valid response. The conversion estimate WILL NOT be shown.", error );
            });
        }


        /**
         * Adds calculated values (`total`) and links external objects
         * (`currency`).
         */
        function extendFeesModel() {
            vm.fees.total       = (vm.fees.tuition || 0) + (vm.fees.levies || 0) + (vm.fees.extraCosts || 0);
            vm.fees.currency    = ContentSwitchingService.getDefaultDomesticCurrency();
        }


        /**
         * Updates the information about the current exchange rate based
         * on selected currency.
         */
        function updateExchangeRateForSelectedCurrency() {
            logger.debug( "Setting exchange rate %s.", exchangeRatesObject[ vm.selectedCurrency.code ] );

            vm.exchangeRate = {
                value:    exchangeRatesObject[ vm.selectedCurrency.code ],
                currency: vm.selectedCurrency
            };
        }


        /**
         * Retrieves a {Promise} with a programme information from the International API.
         *
         * @returns {Promise}
         */
        function getProgrammePromise() {
            return DataService.getInternationalProgramme( vm.programmeCode, ContentSwitchingService.userSettings.lastStudyCountry.code );
        }


        /**
         * Generates log with detailed information about given fees' metadata.
         */
        function getLogIntro() {
            try {
                var logIntro = '',

                qual = Utils.retrieveMetadataRecursively( $scope ),

                parentalDynamicBlock = $scope.$parent.$parent.$parent;

                // Inside a sub-qualification (~ nested)
                if ( parentalDynamicBlock && parentalDynamicBlock.mode === 'nested' ) {
                    logIntro = '{NESTED} ';
                }
                // As a top-level component
                else {
                    logIntro = '{TOP-LEVEL} ';
                }

                logIntro += 'Fees (International API) for `' + qual.name + '`: \n' +
                ' > Qual. code -> API code: ' + qual.code + ' -> ' + vm.programmeCode + '\n' +
                ' > Country code of user\'s nationality: ' + ContentSwitchingService.userSettings.nationalityCountry.code + '\n\n';
            } catch ( err ) {
                logger.error( "Rich log message could not be constructed.", err );
            }

            return logIntro;
        }


    }



    FeesController.$inject = [ '$q', '$scope', '$filter', 'ContentSwitchingService', 'DataService', 'LoggerFactory', 'SubpagesService', 'App', 'Utils', 'TEXTS' ];
})();
